import { useModal } from '@jutro/components';
import { useTranslator } from '@jutro/locale';
import { MetadataContent } from '@jutro/uiconfig';
import { useDependencies } from 'gw-portals-dependency-react';
import {
    ViewModelServiceContext,
    withViewModelService
} from 'gw-portals-viewmodel-react';
import React, { useContext, useEffect, useState, useCallback } from 'react';
import { withRouter } from 'react-router-dom';
import platformMessages from 'gw-platform-translations/platform.messages';
import _ from 'lodash';
import { AdacConfirmationModal, AdacButtonWithLoader, CommunicationHistory, CommunicationVariablesContext } from 'gw-capability-fnol-adac-react';
import { ErrorCodes } from 'gw-portals-util-adac-js';
import metadata from './SendNotePage.metadata.json5';
import styles from './SendNotePage.module.scss';
import messages from './SendNotePage.messages';
import wizardMessages from '../../FNOLAdacWizard.messages';
import usePortalModeBasedLabel from '../../hooks/usePortalModeBasedLabel';

function SendNotePage({ history }) {
    const { ClaimService } = useDependencies('ClaimService');
    const { communicationVariables } = useContext(CommunicationVariablesContext)
    const { token, sessionUUID, communicationTypeExt } = communicationVariables;
    const viewModelService = useContext(ViewModelServiceContext);
    const [callbackNeeded, setCallbackNeeded] = useState(false);
    const [noteVM, setNoteVM] = useState();
    const [portalRecallVM, setPortalRecallVM] = useState();
    const [notesHistory, setNotesHistory] = useState([]);
    const [processing, setProcessing] = useState(false);
    const [loaded, setLoaded] = useState(false);
    const { showModal } = useModal();
    const translator = useTranslator();

    const showModalAndGoBack = useCallback(async () => {
        history.goBack();
        showModal(
            <AdacConfirmationModal
                icon="mi-info"
                message={translator(messages.activityOpenError)}
                confirmButtonText={translator(platformMessages.close)}
            />
        );
    }, [history, translator]);

    useEffect(() => {
        ClaimService.retrieveNotesHistory(token, sessionUUID)
            .then((response) => {
                if (response.hasUnansweredNote) {
                    showModalAndGoBack();
                } else {
                    const vm = viewModelService.create({body: '', subject: 'Portal note'},
                        'cc',
                        'edge.capabilities.note.dto.NoteDTO'
                    );
                    setNoteVM(vm);
                    setNotesHistory(response.notesHistory);
                }
            })
            .catch(() => {
                history.push({ pathname: '/trylater' });
            })
            .finally(() => {
                setLoaded(true);
            });
    }, []);

    const onNoteValueChange = (value) => {
        const newVM = viewModelService.clone(noteVM);
        _.set(newVM, 'body.value', value);
        setNoteVM(newVM);
    };

    const onRecallValueChange = (value) => {
        const newVM = viewModelService.clone(portalRecallVM);
        _.set(newVM, 'hours.value', value);
        setPortalRecallVM(newVM);
    };

    const showConfirmationModal = async () => {
        history.push({
            pathname: '/history'
        })
        showModal(
            <AdacConfirmationModal
                message={translator(messages.noteSentConfirmation)}
                confirmButtonText={translator(platformMessages.close)}
            />
        );
    };

    const sendNote = () => {
        setProcessing(true);
        ClaimService.sendNote(noteVM.value, _.get(portalRecallVM, 'value'), token, sessionUUID)
            .then(showConfirmationModal)
            .catch((error) => {
                if (error.appErrorCode === ErrorCodes.SESSION_EXPIRED) {
                    history.push({
                        pathname: '/sessionexpired'
                    });
                } else {
                    history.push({
                        pathname: '/callcenter'
                    });
                }
            })
            .finally(() => setProcessing(false));
    };

    const onCallbackNeededChecked = (value) => {
        setCallbackNeeded(value);
        if (!value) {
            setPortalRecallVM(null);
        } else {
            const vm = viewModelService.create({},
                'cc',
                'adac.edge.capabilities.claim.fnol.dto.PortalRecallOptionDTO');
            setPortalRecallVM(vm);
        }
    };

    const isValid = _.get(noteVM, 'aspects.subtreeValid') && (_.get(noteVM, 'aspects.subtreeValid') || !callbackNeeded);

    const overrides = {
        pageHeaderTitle: {
            content: usePortalModeBasedLabel('pageHeaderTitle')
        },
        callbackNeededCheckbox: {
            onValueChange: onCallbackNeededChecked,
            value: callbackNeeded
        },
        recallContainer: {
            visible: callbackNeeded
        },
        noteField: {
            onValueChange: onNoteValueChange,
            value: _.get(noteVM, 'value.body')
        },
        callOptionsCard: {
            value: _.get(portalRecallVM, 'value.hours'),
            onValueChange: onRecallValueChange,
        },
        nextButton: {
            onClick: sendNote,
            disabled: !isValid || processing,
            isLoading: processing,
            label: translator(wizardMessages.next)
        },
        backButton: {
            onClick: () => history.goBack()
        },
        loader: {
            loaded
        },
        pageContainer: {
            visible: loaded
        },
        notesHistory: {
            dataToDisplay: notesHistory,
            filtersToBeShown: ['from']
        }
    };

    const resolvers = {
        resolveComponentMap: {
            buttonWitLoader: AdacButtonWithLoader,
            CommunicationHistory
        },
        resolveClassNameMap: styles,
        resolveCallbackMap: {}
    };
    return <MetadataContent
        uiProps={metadata.pageContent}
        overrideProps={overrides}
        {...resolvers}
    />;
}

export default withViewModelService(withRouter(SendNotePage));
